// =============================================================================
// Global Topbar - Main app bar (ClickUp style - detached from content)
// Contains: Logo | Workspace + Calendar | Search | Quick Add + Avatar
// =============================================================================

import React, { useState } from "react";
import { NotificationBell } from "@/modules/project/ui";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useUserStatusContext } from "@/contexts/UserStatusContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { StatusAvatar } from "@/components/ui/status-avatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Search, LogOut, User, Calendar, ChevronDown, Settings, Sparkles, Check } from "lucide-react";
import { ERP_COLORS } from "@/config/design-system";

const COLORS = { ...ERP_COLORS };

export function GlobalTopbar() {
  const navigate = useNavigate();
  const { profile, signOut, user } = useAuth();
  const { currentOrganization, organizations, setCurrentOrganization, currentRole } = useOrganization();
  const canAccessSettings = currentRole === 'owner' || currentRole === 'admin';
  const { getStatus, currentMemberId } = useUserStatusContext();

  const userStatus = currentMemberId ? getStatus(currentMemberId) : 'available';

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  const getInitials = (name: string | null) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const openCommandPalette = () => {
    const event = new KeyboardEvent("keydown", {
      key: "k",
      metaKey: true,
      ctrlKey: true,
      bubbles: true,
    });
    document.dispatchEvent(event);
  };

  return (
    <header data-tour="topbar" className="h-[18px] flex items-center pl-0 pr-3 shrink-0 overflow-visible">
      {/* Left: Workspace + Calendar */}
      <div className="flex items-center gap-1.5">
        {/* Workspace Selector */}
        {currentOrganization && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 gap-1.5 px-2 rounded-md border border-border/50 bg-muted hover:bg-muted/80"
              >
                <div
                  className="h-5 w-5 rounded-sm flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                  style={{ backgroundColor: COLORS.accentPrimary }}
                >
                  {currentOrganization.name.charAt(0).toUpperCase()}
                </div>
                <span
                  className="font-medium text-xs hidden md:inline max-w-[150px] truncate"
                  style={{ color: COLORS.textPrimary }}
                >
                  {currentOrganization.name}
                </span>
                <ChevronDown className="h-3 w-3 opacity-50" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64" style={{ backgroundColor: COLORS.bgTopbar }}>
              <DropdownMenuLabel style={{ color: COLORS.textSecondary }}>Mes workspaces</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {organizations.map((org) => {
                const isActive = org.id === currentOrganization.id;
                return (
                  <DropdownMenuItem
                    key={org.id}
                    className="cursor-pointer flex items-center gap-2"
                    onClick={() => {
                      if (!isActive) {
                        setCurrentOrganization(org);
                        navigate("/app");
                      }
                    }}
                  >
                    <div
                      className="h-5 w-5 rounded-sm flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                      style={{ backgroundColor: COLORS.accentPrimary }}
                    >
                      {org.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="flex-1 truncate" style={{ color: COLORS.textPrimary }}>{org.name}</span>
                    {isActive && <Check className="h-3.5 w-3.5" style={{ color: COLORS.accentPrimary }} />}
                  </DropdownMenuItem>
                );
              })}
              {canAccessSettings && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/admin/settings")}>
                    <Settings className="h-4 w-4 mr-2" style={{ color: COLORS.iconDefault }} />
                    <span style={{ color: COLORS.textSecondary }}>Paramètres du workspace</span>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Calendar quick access → Planning */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" className="h-5 w-5 hidden md:flex" onClick={() => navigate("/planning")}>
              <Calendar className="h-3 w-3" style={{ color: COLORS.iconDefault }} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Planification</TooltipContent>
        </Tooltip>
      </div>

      {/* Center: Search */}
      <div className="flex-1 flex justify-center px-2">
        <div className="relative w-full max-w-[220px] hidden md:block">
          <Search
            className="absolute left-2 top-1/2 -translate-y-1/2 h-2.5 w-2.5"
            style={{ color: COLORS.iconDefault }}
          />
          <Input
            placeholder="Rechercher..."
            className="pl-6 h-6 border border-border focus-visible:ring-1 text-[10px] bg-white rounded-full placeholder:text-gray-600"
            style={{
              color: COLORS.textPrimary,
            }}
            readOnly
            onClick={openCommandPalette}
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <kbd className="hidden sm:inline-flex h-3 items-center gap-0.5 rounded border bg-muted px-1 font-mono text-[8px] font-medium text-muted-foreground">
              <span className="text-[8px]">⌘</span>K
            </kbd>
          </div>
        </div>
      </div>

      {/* Right: Avatar */}
      <div className="flex items-center gap-1.5 overflow-visible relative z-50">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-muted-foreground"
              data-tour="tour-restart"
              onClick={() => {
                if (user?.id) {
                  localStorage.removeItem(`lapyno_product_tour_completed:${user.id}`);
                  localStorage.setItem(`lapyno_product_tour_eligible:${user.id}`, 'true');
                }
                window.dispatchEvent(new CustomEvent('lapyno:start-tour'));
              }}
            >
              <Sparkles className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Guide de démarrage</TooltipContent>
        </Tooltip>
        <div data-tour="notifications"><NotificationBell /></div>

        {/* User Avatar */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 rounded-full"
              data-testid="topbar-user-menu-trigger"
            >
              <StatusAvatar
                src={profile?.avatar_url}
                fallback={getInitials(profile?.full_name)}
                status={userStatus}
                size="sm"
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" style={{ backgroundColor: COLORS.bgTopbar }}>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none" style={{ color: COLORS.textPrimary }}>
                  {profile?.full_name || "Utilisateur"}
                </p>
                <p className="text-xs leading-none" style={{ color: COLORS.textSecondary }}>
                  {profile?.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/profile")}>
              <User className="mr-2 h-4 w-4" style={{ color: COLORS.iconDefault }} />
              <span style={{ color: COLORS.textPrimary }}>Mon profil</span>
            </DropdownMenuItem>
            {canAccessSettings && (
              <DropdownMenuItem className="cursor-pointer" onClick={() => navigate("/admin/settings")}>
                <Settings className="mr-2 h-4 w-4" style={{ color: COLORS.iconDefault }} />
                <span style={{ color: COLORS.textPrimary }}>Paramètres</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={handleSignOut}
              data-testid="topbar-signout"
            >
              <LogOut className="mr-2 h-4 w-4" style={{ color: "#EF4444" }} />
              <span style={{ color: "#EF4444" }}>Déconnexion</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
